import React, { useState, useEffect } from "react";
import {
  List,
  EditButton,
  DeleteButton,
  CreateButton,
  TopToolbar,
  useListContext,
  useNotify,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ShopItem } from "../types";
import { shopItemApi } from "../../dataProvider/resources/shop";
import { useClub } from "../../club/ClubContext";

function ShopItemActions() {
  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  );
}

function SortButtons({ items, index, onMove }: { items: ShopItem[]; index: number; onMove: (from: number, to: number) => void }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Tooltip title="Nach oben">
        <span>
          <IconButton size="small" disabled={index === 0} onClick={(e) => { e.stopPropagation(); onMove(index, index - 1); }}>
            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Nach unten">
        <span>
          <IconButton size="small" disabled={index === items.length - 1} onClick={(e) => { e.stopPropagation(); onMove(index, index + 1); }}>
            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function SortableItemList({ isSmall }: { isSmall: boolean }) {
  const { data, isPending } = useListContext<ShopItem>();
  const notify = useNotify();
  const { clubId } = useClub();
  const [items, setItems] = useState<ShopItem[]>([]);

  useEffect(() => {
    if (data) {
      setItems([...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    }
  }, [data]);

  if (isPending || !items.length) return null;

  const handleMove = async (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const reordered = [...items];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    setItems(reordered);
    try {
      await shopItemApi.reorder(
        clubId!,
        reordered.map((item, i) => ({ id: item.id, sort_order: i }))
      );
    } catch {
      setItems(items);
      notify("Fehler beim Speichern der Reihenfolge", { type: "error" });
    }
  };

  if (isSmall) {
    return (
      <Box>
        {items.map((item, index) => (
          <Card key={item.id} sx={{ mb: 1 }}>
            <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", pb: "12px !important" }}>
              <SortButtons items={items} index={index} onMove={handleMove} />
              {item.image_url ? (
                <Avatar src={item.image_url} variant="rounded" sx={{ width: 56, height: 56 }} />
              ) : (
                <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: "action.hover" }}>
                  <ImageIcon color="disabled" />
                </Avatar>
              )}
              <Box flex={1} minWidth={0}>
                <Typography fontWeight={600} noWrap>
                  {item.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  € {Number(item.price).toFixed(2)}
                  {!item.active && " · Inaktiv"}
                  {item.has_imprint && " · Aufdruck"}
                </Typography>
                {item.sizes && item.sizes.length > 0 && (
                  <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                    {item.sizes.map((s) => (
                      <Chip key={s} label={s} size="small" />
                    ))}
                  </Stack>
                )}
              </Box>
              <EditButton record={item} label="" />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", px: 2, py: 1, bgcolor: "action.hover", borderRadius: 1, mb: 0.5 }}>
        <Box sx={{ width: 48 }} />
        <Box sx={{ width: 64, mr: 2 }} />
        <Box flex={1}><Typography variant="caption" color="text.secondary">Name</Typography></Box>
        <Box sx={{ width: 80, mr: 2 }}><Typography variant="caption" color="text.secondary">Preis</Typography></Box>
        <Box sx={{ width: 160, mr: 2 }}><Typography variant="caption" color="text.secondary">Größen</Typography></Box>
        <Box sx={{ width: 70, mr: 2 }}><Typography variant="caption" color="text.secondary">Aufdruck</Typography></Box>
        <Box sx={{ width: 50, mr: 2 }}><Typography variant="caption" color="text.secondary">Aktiv</Typography></Box>
        <Box sx={{ width: 80 }} />
      </Box>
      {items.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box sx={{ width: 48 }}>
            <SortButtons items={items} index={index} onMove={handleMove} />
          </Box>
          <Box sx={{ width: 64, mr: 2 }}>
            {item.image_url ? (
              <Avatar src={item.image_url} variant="rounded" sx={{ width: 48, height: 48 }} />
            ) : (
              <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: "action.hover" }}>
                <ImageIcon color="disabled" />
              </Avatar>
            )}
          </Box>
          <Box flex={1}>
            <Typography fontWeight={500}>{item.name}</Typography>
          </Box>
          <Box sx={{ width: 80, mr: 2 }}>
            <Typography variant="body2">€ {Number(item.price).toFixed(2)}</Typography>
          </Box>
          <Box sx={{ width: 160, mr: 2 }}>
            {item.sizes?.length ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {item.sizes.map((s) => <Chip key={s} label={s} size="small" />)}
              </Stack>
            ) : "–"}
          </Box>
          <Box sx={{ width: 70, mr: 2 }}>
            <Typography variant="body2">{item.has_imprint ? "✓" : "–"}</Typography>
          </Box>
          <Box sx={{ width: 50, mr: 2 }}>
            <Typography variant="body2">{item.active ? "✓" : "–"}</Typography>
          </Box>
          <Box sx={{ width: 80, display: "flex", gap: 0.5 }}>
            <EditButton record={item} label="" />
            <DeleteButton record={item} label="" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function ShopItemList() {
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));

  return (
    <List actions={<ShopItemActions />} title="Artikel verwalten" perPage={500} sort={{ field: "sort_order", order: "ASC" }}>
      <SortableItemList isSmall={isSmall} />
    </List>
  );
}
